import React, { useEffect, useState } from 'react';

import { useParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { t } from 'i18next';

import { useMarketsContext } from './MarketContext';

import AssetPerformanceWidget from '../Widget/AssetPerformance/AssetPerformance';
import { Icon, Loading } from '../UI';
import { MetricsWidget } from './Widgets/Metrics';
import { MarketDetailBar } from './MarketDetailBar';
import { MarketTechnicalIndicator } from './Widgets/MarketTechnicalIndicator';
import { News } from './Widgets/News';
import { Tweets } from './Widgets/Tweets';
import { WrapperMarketWidget } from './WrapperMarketWidget';
import { YoutubeNews } from './Widgets/YoutubeNews';

import {
  GET_ASSET,
  GET_NEWS,
  WIDGET_REDDIT,
  WIDGET_TWITTER,
  WIDGET_YOUTUBE_SEARCH,
  WIDGET_WIKIPEDIA,
} from '../../http/apollo-queries';

import { MarketAssetType } from './types';
import {
  DataNews,
  DataReddit,
  DataTwitter,
  DataWikipedia,
  DataYoutube,
} from '../Widget/Social/types';
import { IAsset, IMembershipPlan } from '../../types/model';
import { logger } from '../../helpers/logger';
import { useMembership } from '../../context/Membership';
import { UpgradeSubscription } from '../UpgradeSubscription/UpgradeSubscription';
import { RedditNews } from './Widgets/RedditNews';
import { IWidgetWrapper } from '../Widget/types';
import { WidgetsGrid } from '../Widget/WidgetsGrid';
import { WikipediaPages } from './Widgets/WikipediaNews';

const twitterIcon = (
  <Icon
    className="flex text-black cursor-pointer ml-2 w-4"
    svgName={'twitter'}
    color="black"
  />
);
const youtubeIcon = (
  <Icon
    className="flex text-black cursor-pointer ml-2 w-4"
    svgName={'youtube'}
  />
);
const redditIcon = (
  <Icon
    className="flex text-black cursor-pointer ml-2 w-4"
    svgName={'reddit'}
  />
);
const wikipediaIcon = (
  <Icon
    className="flex text-black cursor-pointer ml-2 w-4"
    svgName={'wikipedia'}
  />
);

export const MarketDetail = () => {
  const { id = '', type = '', filter = '' } = useParams()!;

  logger.log('MarketDetail:marketDetail', { id, type, filter });

  const { assetsList, loadingExtractAssets, setInitialFilters } =
    useMarketsContext();

  const { membership, membershipPlans } = useMembership();

  const availableWidgets =
    membership.membershipPlan?.membershipPlanWidgets?.items.map(
      ({ widget }) => widget
    );

  useEffect(() => {
    setInitialFilters(type as MarketAssetType, filter.split('-'));
  }, [type]);

  const { data: { getAsset: asset } = {} } = useQuery<{ getAsset: IAsset }>(
    GET_ASSET,
    {
      variables: { id },
      skip: !id,
    }
  );

  const options = {
    fetchPolicy: 'network-only',
    pollInterval: 60000,
    skip: typeof asset?.name !== 'string',
    variables: { search: [`${asset?.symbol.toUpperCase()}`] },
  } as const;

  const { data: { getNews: { items: posts = [] } = {} } = {} } =
    useQuery<DataNews>(GET_NEWS, {
      ...options,
      variables: {
        tickers: [
          `${asset?.symbol.toUpperCase()}:${asset?.name.toUpperCase()}`,
        ],
      },
    });

  const { data: { widgetTwitter: { tweets = [] } = {} } = {} } =
    useQuery<DataTwitter>(WIDGET_TWITTER, { ...options });

  const { data: { widgetYoutube: { items: youtubeNews = [] } = {} } = {} } =
    useQuery<DataYoutube>(WIDGET_YOUTUBE_SEARCH, { ...options });

  const { data: { widgetReddit: { posts: redditNews = [] } = {} } = {} } =
    useQuery<DataReddit>(WIDGET_REDDIT, { ...options });

  const {
    data: { widgetWikipedia: { items: wikipediaPages = [] } = {} } = {},
  } = useQuery<DataWikipedia>(WIDGET_WIKIPEDIA, {
    ...options,
    variables: {
      search: [`${asset?.symbol.toUpperCase()}:${asset?.name.toUpperCase()}`],
    },
  });

  const [footer, setFooter] = useState<JSX.Element | undefined>(<></>);

  const marketWidgets = [
    {
      key: 'PERFORMANCE',
      width: 4,
      children: (
        <AssetPerformanceWidget
          asset={asset}
          width={0}
          standAlone={true}
          height={1}
          loading={() => {}}
          setLegend={setFooter}
        />
      ),
    },
    {
      key: 'OVERVIEW',
      width: 2,
      children: <MetricsWidget table="overview" />,
    },
    // {
    //   key: 'NEWS',
    //   widget: 'News',
    //   width: 3,
    //   children: <News posts={posts} />,
    // },
    {
      key: 'TWITTER',
      widget: 'Twitter',
      width: 3,
      children: <Tweets tweets={tweets} />,
    },
    {
      key: 'YOUTUBE',
      widget: 'Youtube',
      width: 3,
      children: <YoutubeNews youtubeNews={youtubeNews} />,
    },
    {
      key: 'REDDIT',
      widget: 'Reddit',
      width: 3,
      children: <RedditNews redditNews={redditNews} />,
    },
    {
      key: 'WIKIPEDIA',
      widget: 'Wikipedia',
      width: 3,
      children: <WikipediaPages wikipediaPages={wikipediaPages} />,
    },
    {
      key: 'TECHNICAL_INDICATOR',
      widget: 'TechnicalIndicator',
      width: 3,
      children: <MarketTechnicalIndicator asset={asset} />,
    },

    ...(asset?.type !== 'FOREX'
      ? [
          {
            key: 'METRICS',
            width: 2,
            children: <MetricsWidget table="metrics" />,
          },
        ]
      : []),
    ...(['STOCK', 'ETF'].includes(asset?.type ?? '')
      ? [
          {
            key: 'FINANCIALS',
            width: 2,
            children: <MetricsWidget table="financials" />,
          },
        ]
      : []),
    ...(asset?.type === 'STOCK'
      ? [
          {
            key: 'DIVIDENDS',
            width: 2,
            children: <MetricsWidget table="dividents" />,
          },
        ]
      : []),
  ];

  const lastFetchDate = new Date();

  const gridColsPerBreakpoint = { xl: 6, lg: 6, md: 6, sm: 3, xs: 1 };

  const isAvailableForSubscription = (widget): boolean => {
    return (
      !widget ||
      !availableWidgets ||
      availableWidgets.findIndex((w) => w?.componentName === widget) > -1
    );
  };

  const availableOnMembershipPlan = (widget): IMembershipPlan | undefined => {
    return membershipPlans.find((membershipPlan) => {
      return !!membershipPlan.membershipPlanWidgets?.items?.filter(
        (membershipPlanWidget) => {
          return membershipPlanWidget.widget.componentName === widget;
        }
      ).length;
    });
  };

  return loadingExtractAssets || !asset ? (
    <div
      className={`
        flex
        rounded-md
        items-centerX
        absolute
        w-full
        h-full
        bg-black-backdrop
        z-100
        text-center
        text-white
        top-0
        left-0
      `}
    >
      <Loading
        status={loadingExtractAssets}
        size={'large'}
        className={'text-center m-auto'}
      />
    </div>
  ) : (
    <div
      className={`
        flex flex-col bg-white w-full dark:bg-black-real dark:text-white
      `}
    >
      <MarketDetailBar asset={asset} />

      <div className={'w-full -mx-4'}>
        <WidgetsGrid
          gridColsPerBreakpoint={gridColsPerBreakpoint}
          resizableActive={false}
          widgets={marketWidgets.map(
            (marketWidget, index): IWidgetWrapper => ({
              id: marketWidget.key,
              width: marketWidget.width,
              height: 1,
              locked: true,
              children: (
                <WrapperMarketWidget
                  width={marketWidget.width}
                  colSpan={4}
                  lastUpdateDate={lastFetchDate}
                  children={
                    isAvailableForSubscription(marketWidget.widget) ? (
                      marketWidget.children
                    ) : (
                      <UpgradeSubscription
                        viewMode={'card'}
                        feature={'MARKET_WIDGET'}
                        textVars={{
                          plan: availableOnMembershipPlan(marketWidget.widget)
                            ?.title,
                        }}
                      />
                    )
                  }
                  footer={!index ? footer : undefined}
                  key={'WrapperMarketWidget_' + index}
                  title={t(`markets.widgets.${marketWidget.key}`)}
                  titleIcon={
                    marketWidget.key === 'TWITTER'
                      ? twitterIcon
                      : marketWidget.key === 'YOUTUBE'
                      ? youtubeIcon
                      : marketWidget.key === 'WIKIPEDIA'
                      ? wikipediaIcon
                      : marketWidget.key === 'REDDIT'
                      ? redditIcon
                      : undefined
                  }
                />
              ),
            })
          )}
        />
      </div>
    </div>
  );
};
